import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react"
import { ArrowsInSimple, ArrowsOutSimple, Info, Storefront } from "@phosphor-icons/react"
import { z } from "zod"

import {
  Badge,
  Button,
  EmptyState,
  ExpandableCard,
  ExpandableCardContent,
  ExpandableCardHeader,
  IconButton,
} from "~/components/design-system/components"
import { usePermission } from "~/hooks/usePermission"
import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"
import { FilterScopeProvider } from "~/providers/FilterScopeProvider"
import { cn } from "~/utils/helpers"
import { showErrorToast } from "~/utils/toaster"
import { CardTitle, PACKAGE_CARD_KEY } from "./PackageCardTitle"
import { StoreClassBadge } from "./SharedComponents"
import {
  AssignSetCheckbox,
  StoreAssignmentRows,
  StoreAssignmentToolbar,
  useStoreAssignment,
  type StoreAssignmentColumn,
  type StoreAssignmentRow,
} from "./StoreAssignmentTable"
import { gradeChips, type GradeChip } from "./storeAssignmentModel"
import { COUNTRY_TO_ZONE, type PackageFolder, type RangePackage } from "./types"
import { noteRunAfterCurrent, usePackageStoresDraft } from "./setup/setupDrafts"
import { useAllocationMutations, useAllocationStores } from "./useAllocationData"

type PackageAssignmentRow = StoreAssignmentRow & {
  classification: string
  size: string
  format: string
  country: string
  zone: string
  floorArea: number
}

const PACKAGE_ASSIGNMENT_COLUMNS: StoreAssignmentColumn<PackageAssignmentRow>[] = [
  {
    field: "classification",
    name: "Store grade",
    type: "string",
    className: "w-18",
    render: (row) => <StoreClassBadge classification={row.classification} />,
  },
  { field: "size", name: "Store size", type: "string", className: "w-16" },
  { field: "format", name: "Store format", type: "string", className: "w-20" },
  { field: "city", name: "City", type: "string", className: "w-24 text-fg-muted" },
  { field: "country", name: "Country", type: "string", className: "w-20" },
  { field: "zone", name: "Zone", longName: "Transfer zone", type: "string", className: "w-16" },
  {
    field: "floorArea",
    name: "Selling area (m2)",
    type: "number",
    className: "w-26 text-right tabular-nums",
  },
]

export const STORES_SCOPE = "allocation-planning.package-stores"
const STORES_CARD_KEY = `${PACKAGE_CARD_KEY}.stores`
const expandedSchema = z.boolean()

function GradeChips({
  chips,
  disabledReason,
  onAssign,
}: {
  chips: GradeChip[]
  disabledReason: string | undefined
  onAssign: (storeIds: string[], assign: boolean) => void
}) {
  const { allowed } = usePermission("edit")
  const locked = disabledReason !== undefined || !allowed
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="paragraph-xs mr-0.5 text-fg-muted">Select by grade</span>
      {chips.map((chip) => (
        <label
          key={chip.key}
          className={cn(
            "label-sm flex h-7 items-center gap-1.5 rounded-full border pr-2.5 pl-1.5 text-fg-base",
            !locked && "cursor-pointer",
            chip.assigned > 0
              ? "border-badge-primary-border bg-badge-primary-bg"
              : cn("border-base bg-base", !locked && "hover:bg-subtle"),
          )}
        >
          <AssignSetCheckbox
            ids={chip.ids}
            state={chip.state}
            onAssign={onAssign}
            disabledReason={disabledReason}
            hint={
              chip.state === "all"
                ? `Unassign every ${chip.label} store`
                : `Assign every ${chip.label} store`
            }
          />
          {chip.label}
          <span className="text-fg-muted tabular-nums">
            {chip.assigned}/{chip.ids.length}
          </span>
        </label>
      ))}
    </div>
  )
}

function useEscape(active: boolean, onEscape: () => void) {
  const onEscapeRef = useRef(onEscape)
  useEffect(() => {
    onEscapeRef.current = onEscape
  })
  useEffect(() => {
    if (!active) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) onEscapeRef.current()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [active])
}

export function useStoresCardExpanded(pkg: RangePackage) {
  return useSafeLocalStorage(STORES_CARD_KEY, pkg.storeIds.length === 0, expandedSchema)
}

function stopToggle(event: MouseEvent) {
  event.stopPropagation()
}

export function PackageStoresCard({
  pkg,
  folders,
  disabledReason,
}: {
  pkg: RangePackage
  folders: PackageFolder[]
  disabledReason: string | undefined
}) {
  const draft = usePackageStoresDraft()
  const save = useAllocationMutations().savePackagePage
  const followed = folders.find((folder) => folder.id === pkg.storesFolderId)
  const following = pkg.storesFolderId !== null
  const assignedIds = following ? new Set(pkg.storeIds) : draft.storeIdsOf(pkg.id)
  const setOwn = (ownStores: boolean) =>
    save.mutate(
      { op: { kind: "setPackageStoresSource", packageId: pkg.id, ownStores } },
      { onSuccess: (result) => noteRunAfterCurrent(result.run), onError: showErrorToast },
    )
  const source =
    pkg.folderId === null ? undefined : (
      <StoresSource
        followingLabel={followed ? `From ${followed.name}` : undefined}
        actionLabel={following ? "Use own stores" : "Use folder's stores"}
        actionTitle={
          following
            ? "Give this package its own store list, starting from the folder's"
            : "Follow the folder's stores again; this package's own list is replaced"
        }
        disabled={disabledReason !== undefined || save.isPending}
        onAction={() => setOwn(following)}
      />
    )
  return (
    <FilterScopeProvider scopeKey={STORES_SCOPE}>
      <StoresCardBody
        persistKey={STORES_CARD_KEY}
        assignedIds={assignedIds}
        isSaved={(storeId) =>
          following ? assignedIds.has(storeId) : draft.isSaved(pkg.id, storeId)
        }
        onAssign={(storeIds, checked) => draft.toggle(pkg.id, storeIds, checked)}
        disabledReason={
          disabledReason ??
          (following
            ? `Follows ${followed?.name ?? "its folder"}'s stores. Use own stores to change them.`
            : undefined)
        }
        source={source}
        targetLabel="this package"
        emptyTitle="No stores get this package yet"
      />
    </FilterScopeProvider>
  )
}

/** Where a store list comes from, with the switch between following and owning it. */
export function StoresSource({
  followingLabel,
  actionLabel,
  actionTitle,
  disabled,
  onAction,
}: {
  followingLabel: string | undefined
  actionLabel: string
  actionTitle: string
  disabled: boolean
  onAction: () => void
}) {
  return (
    <div className="flex items-center gap-2" onClick={stopToggle}>
      <Badge variant={followingLabel ? "neutral" : "violet"}>
        {followingLabel ?? "Own stores"}
      </Badge>
      <Button
        variant="transparent"
        size="sm"
        requires="edit"
        allowedTitle={actionTitle}
        disabled={disabled}
        onClick={onAction}
      >
        {actionLabel}
      </Button>
    </div>
  )
}

/**
 * The Stores card body, for a package's staged list and a folder's list alike:
 * the caller owns what is assigned and what a tick does.
 */
export function StoresCardBody({
  persistKey,
  assignedIds,
  isSaved,
  onAssign,
  disabledReason,
  source,
  footer,
  targetLabel,
  emptyTitle,
}: {
  persistKey: string
  assignedIds: ReadonlySet<string>
  isSaved: (storeId: string) => boolean
  onAssign: (storeIds: string[], assign: boolean) => void
  disabledReason: string | undefined
  /** Beside the title: where the list comes from. */
  source?: ReactNode
  /** Under the table, e.g. the folder's Apply. */
  footer?: ReactNode
  targetLabel: string
  emptyTitle: string
}) {
  const { stores: allStores, isLoading: storesLoading } = useAllocationStores()
  const [expanded, setExpanded] = useSafeLocalStorage(
    persistKey,
    assignedIds.size === 0,
    expandedSchema,
  )
  const [fullScreen, setFullScreen] = useState(false)
  useEscape(fullScreen, () => setFullScreen(false))

  const rows = useMemo<PackageAssignmentRow[]>(
    () =>
      allStores.map((store) => ({
        id: store.id,
        storeName: store.storeNumber,
        city: store.city,
        checked: assignedIds.has(store.id),
        changed: assignedIds.has(store.id) !== isSaved(store.id),
        classification: store.classification,
        size: store.size,
        format: store.type,
        country: store.country,
        zone: store.zone ?? COUNTRY_TO_ZONE[store.country] ?? "—",
        floorArea: store.floorArea,
      })),
    [allStores, assignedIds, isSaved],
  )
  const view = useStoreAssignment({
    scopeKey: STORES_SCOPE,
    rows,
    columns: PACKAGE_ASSIGNMENT_COLUMNS,
    onAssign,
    disabledReason,
    groupable: true,
  })
  const chips = gradeChips(rows)
  const empty = assignedIds.size === 0 && !storesLoading && allStores.length > 0
  const open = expanded || fullScreen

  return (
    <ExpandableCard
      className={cn("shrink-0", fullScreen && "fixed inset-0 z-40 flex flex-col rounded-none")}
      expanded={open}
      onExpandedChange={(next) => {
        if (!next) setFullScreen(false)
        setExpanded(next)
      }}
    >
      <ExpandableCardHeader>
        <Storefront size={16} className="text-fg-muted" />
        <CardTitle meta={`${assignedIds.size} out of ${allStores.length} stores assigned`}>
          Stores
        </CardTitle>
        {source}
        <div className="flex-1" />
        {open && (
          <div
            role="toolbar"
            aria-label="Store table controls"
            className="flex items-center gap-1"
            onClick={stopToggle}
          >
            <StoreAssignmentToolbar view={view} />
            <IconButton
              variant="neutral"
              size="sm"
              aria-label={fullScreen ? "Exit full screen" : "Full screen"}
              title={fullScreen ? "Exit full screen (Esc)" : "Full screen"}
              onClick={() => setFullScreen(!fullScreen)}
            >
              {fullScreen ? <ArrowsInSimple /> : <ArrowsOutSimple />}
            </IconButton>
          </div>
        )}
      </ExpandableCardHeader>
      <ExpandableCardContent fill={fullScreen}>
        <div className={cn("flex flex-col gap-3", fullScreen && "min-h-0 flex-1")}>
          {empty && (
            <EmptyState
              icon={Storefront}
              title={emptyTitle}
              description="Nothing replenishes until stores are assigned. Assign every store, start from a store grade, or tick stores in the table below."
              action={{
                label: `Assign all ${allStores.length} stores`,
                onClick: () =>
                  view.onAssign(
                    allStores.map((store) => store.id),
                    true,
                  ),
                requires: "edit",
                disabled: disabledReason !== undefined,
                allowedTitle: disabledReason,
              }}
              className="py-6"
            />
          )}
          <div className="paragraph-xs flex items-center gap-1.5 text-fg-muted">
            <Info className="size-3.5 shrink-0" />
            <span>
              {`An assigned store is expected to carry the full range of ${targetLabel}. If a store should carry only part of it, create a separate package with fewer products instead.`}
            </span>
          </div>
          {chips.length > 0 && (
            <GradeChips chips={chips} disabledReason={disabledReason} onAssign={view.onAssign} />
          )}
          <StoreAssignmentRows
            view={view}
            targetLabel={targetLabel}
            loading={storesLoading}
            bounded={!fullScreen}
            fill={fullScreen}
          />
          {footer}
        </div>
      </ExpandableCardContent>
    </ExpandableCard>
  )
}
